import { useCallback, useMemo, useState, type ReactElement } from 'react';

import { ResourceGraph, ResourceUsage, TaskUsage } from '../components/resource';
import type { TimeGranularity } from '../components/resource/timephased-grid';
import {
  buildResourceGraphSeries,
  buildResourceUsageGroups,
  buildTaskUsageGroups,
} from '../integration/adapters';
import { selectOverallocations } from '../integration/derivations';
import { useProjectStore } from '../store/project-store';
import { EmptyState } from './EmptyState';

const TABS = ['資源使用狀況', '任務使用狀況', '資源長條圖'] as const;
type Tab = (typeof TABS)[number];

/** 資源使用狀況視圖：逐時段負載 + 長條圖，過度分配的格子由元件自行標紅。 */
export function ResourceUsageView(): ReactElement {
  const project = useProjectStore((state) => state.project);
  const [tab, setTab] = useState<Tab>('資源使用狀況');
  const [granularity, setGranularity] = useState<TimeGranularity>('Day');
  const [collapsed, setCollapsed] = useState<readonly string[]>([]);

  const resourceGroups = useMemo(() => buildResourceUsageGroups(project), [project]);
  const taskGroups = useMemo(() => buildTaskUsageGroups(project), [project]);
  const graphSeries = useMemo(() => buildResourceGraphSeries(project), [project]);
  const overallocations = useMemo(() => selectOverallocations(project), [project]);

  const toggleCollapse = useCallback((groupId: string) => {
    setCollapsed((current) =>
      current.includes(groupId) ? current.filter((id) => id !== groupId) : [...current, groupId],
    );
  }, []);

  const rangeStart = (project.startDate as string).slice(0, 10);
  const rangeEnd = (project.finishDate as string).slice(0, 10);

  if (project.assignments.length === 0) {
    return (
      <EmptyState
        title="尚無資源指派"
        description="把資源指派到任務後，這裡會顯示逐日負載與過度分配。"
        testId="resource-usage-empty"
      />
    );
  }

  return (
    <div data-testid="resource-usage-view" className="flex h-full w-full flex-col gap-2">
      <div className="flex items-center gap-2 text-xs">
        {TABS.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => setTab(name)}
            className={`rounded px-2 py-1 ${
              tab === name
                ? 'bg-blue-600 text-white'
                : 'border border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800'
            }`}
          >
            {name}
          </button>
        ))}
        <span
          data-testid="overallocation-summary"
          className={`ml-auto ${overallocations.length > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}`}
        >
          過度分配區間：{overallocations.length}
        </span>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden rounded border border-slate-200 dark:border-slate-700">
        {tab === '資源使用狀況' ? (
          <ResourceUsage
            groups={resourceGroups}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            granularity={granularity}
            onGranularityChange={setGranularity}
            collapsedGroupIds={collapsed}
            onToggleCollapse={toggleCollapse}
          />
        ) : null}
        {tab === '任務使用狀況' ? (
          <TaskUsage
            groups={taskGroups}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            granularity={granularity}
            onGranularityChange={setGranularity}
            collapsedGroupIds={collapsed}
            onToggleCollapse={toggleCollapse}
          />
        ) : null}
        {tab === '資源長條圖' ? (
          <div className="h-full w-full overflow-auto bg-white p-2 dark:bg-neutral-900">
            <ResourceGraph series={graphSeries} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
